
import { useState, useCallback } from 'react';
import { useStudents } from '@/contexts';
import { EnrollmentEvent, EnrollmentEventType, EnrollmentStatus } from '@/services/db/enrollment/types';
import { 
  getStudentEnrollmentHistory, 
  trackNewEnrollment, 
  trackProgramTransfer, 
  trackStatusChange,
  trackWithdrawal,
  trackReEnrollment
} from '@/services/db/enrollment/enrollmentHistoryService';
import { Student, Program } from '@/services/db/types';
import { toast } from 'sonner';
import { useActionTracking } from './useActionTracking';

export const useEnrollmentHistory = (studentId?: number) => {
  const [events, setEvents] = useState<EnrollmentEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const { refreshStudents } = useStudents();
  const { trackStudentAction } = useActionTracking();
  
  const fetchHistory = useCallback(async (id?: number) => {
    if (!id && !studentId) {
      return [];
    }
    
    const targetId = id || studentId!;
    setLoading(true);
    try {
      console.log(`Fetching enrollment history for student ID: ${targetId}`);
      
      // First refresh students to ensure we have latest data
      await refreshStudents();
      
      // Then fetch enrollment history
      const history = await getStudentEnrollmentHistory(targetId);
      console.log(`Found ${history.length} history records`, history);
      setEvents(history);
      return history;
    } catch (error) {
      console.error('Error fetching enrollment history:', error);
      toast.error('Failed to load enrollment history');
      return [];
    } finally {
      setLoading(false);
    }
  }, [studentId, refreshStudents]);
  
  const recordNewEnrollment = useCallback(async (student: Student, program: Program) => {
    try {
      console.log(`Recording new enrollment for student ${student.id} in program ${program.title}`);
      const event = await trackNewEnrollment(student, program);
      
      // Also track in admin history
      trackStudentAction(
        student.id,
        'Administrator',
        'New enrollment',
        {
          programTitle: program.title,
          programCategory: program.category,
          feeStatus: program.feeStatus,
          enrollmentDate: program.enrolledAt
        }
      );
      
      // Refresh the history after recording
      await fetchHistory(student.id);
      
      return event;
    } catch (error) {
      console.error('Error recording enrollment:', error);
      toast.error('Failed to record enrollment');
      throw error;
    }
  }, [fetchHistory]);
  
  return {
    events,
    loading,
    fetchHistory,
    recordNewEnrollment
  };
};
